import { Autocomplete, Chip, TextField } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useLocation } from "react-router-dom";
import deleteBinIcon from "../../assets/icons/delete-bin.svg";
import errorIcon from "../../assets/icons/error-icon.svg";
import addCircleIcon from "../../assets/icons/plus-icon-magenta.svg";
import CustomDateCheckbox from "../createSegment/CustomDateCheckbox";
import DateField from "../../shared/components/DateField";
import DelieryViewTime from "./DelieryViewTime";
import { useEffect, useMemo, useRef } from "react";
import { useCountrySecrets } from "../../services/destination";
import CustomTooltip from "../../shared/components/custom-tooltip/CustomTooltip";

const STIRISTA_PREFIX = "Stirista-";

const CampaignFormComponent = ({ dataSegment, countryTags }: any) => {
  const types = [
    "One-time",
    "Recurring Daily",
    "Recurring Weekly",
    "Recurring Monthly",
    "Recurring Quarterly",
    "Recurring Yearly",
  ];
  const deliveryTypes = ["Deliver ASAP", "Schedule delivery"];
  const deleteRowTooltip =
  {
    alt: "delete filter",
    icon: deleteBinIcon,
    tooltip: "Delete filter",
  }
  const methods = useFormContext();
  const { control, getValues, setValue, watch, setError, clearErrors } = methods;

  const location = useLocation();
  const currentPath = location.pathname;
  const currentPathSplit = currentPath.split("/");
  const disabled = currentPathSplit?.[1] === "campaign";

  const selectedCountry = watch("country");
  const selectedDate = watch("startDate");
  const timeValue = watch("timeValue");
  const watchedCampaignName = useWatch({ control, name: "campaignName" });
  const watchedInclude = useWatch({ control, name: "include" });
  const watchedExclude = useWatch({ control, name: "exclude" });
  const campaignNameInputRef = useRef<HTMLInputElement | null>(null);
  const prevShouldForceRef = useRef(false);
  const { data: destinationOptions = [], isLoading } = useCountrySecrets({
    headers: { country: selectedCountry, tags: countryTags},
    options: { enabled: !!selectedCountry && !!countryTags?.length }
  });
  const options = Object.entries(destinationOptions).map(([id, val]: [string, any]) => ({
    label: typeof val === "string" ? val : val?.label || id,
    value: id,
    status: typeof val === "string" ? "" : val?.status || "",
    status_text: typeof val === "string" ? "" : val?.status_text || "",
  }));

  useEffect(() => {
    if (!selectedDate) return;

    const now = dayjs();
    const isToday = dayjs(selectedDate).isSame(now, "day");

    if (isToday && !timeValue) {
      setValue("timeValue", now.add(5, "minute").format("HH:mm:ss"));
    }
  }, [selectedDate, timeValue, setValue]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "audienceGroups",
  });

  const segmentNameById = useMemo(() => {
    // Build lookup map to quickly resolve selected segment IDs to names.
    const map = new Map<any, string>();
    const list: any[] = Array.isArray(dataSegment?.data) ? dataSegment.data : [];
    for (const item of list) {
      if (item?.segmentId == null) continue;
      if (typeof item?.segmentName !== "string") continue;
      map.set(item.segmentId, item.segmentName);
    }
    return map;
  }, [dataSegment?.data]);

  const shouldForceStiristaPrefix = useMemo(() => {
    // Campaign name is forced only while selected include/exclude contains Stirista segments.
    const includeIds = Array.isArray(watchedInclude) ? watchedInclude : [];
    const excludeIds = Array.isArray(watchedExclude) ? watchedExclude : [];
    const all = [...includeIds, ...excludeIds];

    return all.some((id) => {
      const name = segmentNameById.get(id);
      return typeof name === "string" && /stirista/i.test(name);
    });
  }, [segmentNameById, watchedExclude, watchedInclude]);

  useEffect(() => {
    const wasForced = prevShouldForceRef.current;
    const current = typeof watchedCampaignName === "string" ? watchedCampaignName : "";

    // Enforce prefix when Stirista segment is selected.
    if (!wasForced && shouldForceStiristaPrefix) {
      if (!current.startsWith(STIRISTA_PREFIX)) {
        const next = `${STIRISTA_PREFIX}${current}`.slice(0, 100);
        setValue("campaignName", next, { shouldDirty: true, shouldTouch: true });
        requestAnimationFrame(() => {
          const el = campaignNameInputRef.current;
          if (!el) return;
          const pos = Math.min(STIRISTA_PREFIX.length, el.value.length);
          el.setSelectionRange(pos, pos);
        });
      }
    }

    if (wasForced && !shouldForceStiristaPrefix) {
      // Remove auto-prefix when no Stirista segment remains selected.
      if (current === STIRISTA_PREFIX) {
        setValue("campaignName", "", { shouldDirty: true, shouldTouch: true });
      } else if (current.startsWith(STIRISTA_PREFIX)) {
        setValue("campaignName", current.slice(STIRISTA_PREFIX.length), {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    }

    prevShouldForceRef.current = shouldForceStiristaPrefix;
  }, [setValue, shouldForceStiristaPrefix, watchedCampaignName]);


  const isHaveDeliveryType = watch("campaignType") === "One-time" ? watch("deliveryType") : true;

  return (
    <>
      <div className="create-campaign__input-wrap">
        <div className="create-campaign__dropdown-wrap__label">Campaign name:</div>

        <div className="form-control-container">
          <Controller
            control={control}
            name="campaignName"
            rules={{
              required: "Please provide campaign name",
              pattern: {
                value: /^(?!\d)[A-Za-z0-9 _ -]+$/,
                message:
                  "Only letters, numbers, underscore (_), hyphen (-) and space are allowed. Cannot start with a number.",
              },
              maxLength: {
                value: 100,
                message: "Segment name cannot exceed 100 characters",
              },
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <>
                  <label ref={field.ref} className="create-campaign__insights-wrap__head">
                    <TextField
                      {...field}
                      value={field.value}
                      inputRef={campaignNameInputRef}
                      className="form-control-container__text-field"
                      required
                      margin="dense"
                      fullWidth
                      variant="outlined"
                      disabled={disabled}
                      placeholder="Add campaign name, e.g. Market Brand"
                      style={{ marginTop: "12px" }}
                      slotProps={{
                        input: {
                          inputProps: { maxLength: 100 },
                        },
                      }}
                      onKeyDown={(e) => {
                        if (!shouldForceStiristaPrefix) return;

                        const input = e.currentTarget as HTMLInputElement;
                        const current = String(input.value || "");
                        const selectionEl = campaignNameInputRef.current ?? input;
                        const start = selectionEl.selectionStart ?? current.length;
                        const end = selectionEl.selectionEnd ?? current.length;
                        const prefixLen = STIRISTA_PREFIX.length;

                        // Prevent deleting locked prefix if input currently equals just the prefix.
                        if ((e.key === "Backspace" || e.key === "Delete") && current === STIRISTA_PREFIX) {
                          e.preventDefault();
                          requestAnimationFrame(() => {
                            const el = campaignNameInputRef.current;
                            if (!el) return;
                            el.setSelectionRange(prefixLen, prefixLen);
                          });
                          return;
                        }

                        const hasSelection = start !== end;
                        const selectionTouchesPrefix = start < prefixLen;

                        // Selection touching prefix keeps prefix and removes only editable suffix.
                        if ((e.key === "Backspace" || e.key === "Delete") && hasSelection && selectionTouchesPrefix) {
                          e.preventDefault();
                          const next = `${STIRISTA_PREFIX}${current.slice(Math.max(end, prefixLen))}`.slice(
                            0,
                            100
                          );
                          setValue("campaignName", next, { shouldDirty: true, shouldTouch: true });
                          requestAnimationFrame(() => {
                            const el = campaignNameInputRef.current;
                            if (!el) return;
                            el.setSelectionRange(prefixLen, prefixLen);
                          });
                          return;
                        }

                        if (e.key === "Backspace" && !hasSelection && start <= prefixLen) {
                          e.preventDefault();
                          requestAnimationFrame(() => {
                            const el = campaignNameInputRef.current;
                            if (!el) return;
                            el.setSelectionRange(prefixLen, prefixLen);
                          });
                          return;
                        }

                        if (e.key === "Delete" && !hasSelection && start < prefixLen) {
                          e.preventDefault();
                          requestAnimationFrame(() => {
                            const el = campaignNameInputRef.current;
                            if (!el) return;
                            el.setSelectionRange(prefixLen, prefixLen);
                          });
                          return;
                        }
                      }}
                      onChange={(e) => {
                        const input = e.target as HTMLInputElement;
                        const raw = input.value;
                        const selectionStart = input.selectionStart ?? raw.length;

                        let next = raw;
                        if (shouldForceStiristaPrefix) {
                          if (
                            raw.length < STIRISTA_PREFIX.length &&
                            STIRISTA_PREFIX.startsWith(raw)
                          ) {
                            next = STIRISTA_PREFIX;
                          } else {
                            const suffix = raw.startsWith(STIRISTA_PREFIX)
                              ? raw.slice(STIRISTA_PREFIX.length)
                              : raw;
                            next = `${STIRISTA_PREFIX}${suffix}`.slice(0, 100);
                          }
                        }

                        if (next.length === 100) {
                          setError("campaignName", {
                            type: "maxLength",
                            message: "Campaign name cannot exceed 100 characters",
                          });
                        } else {
                          clearErrors("campaignName");
                        }

                        field.onChange(next);

                        if (shouldForceStiristaPrefix) {
                          requestAnimationFrame(() => {
                            const el = campaignNameInputRef.current;
                            if (!el) return;
                            const delta = next.length - raw.length;
                            const nextPos = Math.max(
                              STIRISTA_PREFIX.length,
                              Math.min(selectionStart + delta, el.value.length)
                            );
                            el.setSelectionRange(nextPos, nextPos);
                          });
                        }
                      }}
                    />
                  </label>

                  {error?.message && (
                    <div className="create-campaign__input-wrap__segment-err">
                      <img src={errorIcon} alt="Error Icon" />
                      {error?.message}
                    </div>
                  )}
                </>
              );
            }}
          />
        </div>
      </div>
      <div className="create-campaign__divider"></div>
      <div className="create-campaign__dropdown-wrap">
        <div className="create-campaign__dropdown-wrap__label">Audience:</div>

        <div className="bg-white rounded-lg p-4 mb-4">
          <p className="font-lg font-[EnergyBold] mb-4">+ Include</p>

          <Controller
            control={control}
            name="include"
            rules={{
              required:
                'there should be one or more segments selected for "Audience" in order to save and activate the campaign.',
            }}
            render={({ field, fieldState: { error } }) => {
              return (
                <>
                  <Autocomplete
                    multiple
                    {...field}
                    disabled={disabled}
                    value={
                      (field.value || []).filter(
                        (option: any) =>
                          !!dataSegment?.data?.find((i: any) => i?.segmentId === option)?.segmentName
                      )
                    }
                    options={dataSegment?.data?.map((i: any) => i?.segmentId) || []}
                    getOptionKey={(option: any) => {
                      return option;
                    }}
                    getOptionLabel={(option: any) =>{
                      return dataSegment?.data?.find((i: any) => i?.segmentId === option)?.segmentName
                    }}
                    onChange={(_event, value: any) => {
                      const valid = value.filter(
                        (option: any) =>
                          !!dataSegment?.data?.find((i: any) => i?.segmentId === option)?.segmentName
                      );
                      field.onChange(valid);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select one or more segments"
                      />
                    )}
                    slotProps={{
                      chip: {
                        size: "small",
                      }
                    }}
                  />

                  {error?.message && (
                    <div className="create-segment__input-wrap__segment-err">
                      <img src={errorIcon} alt="Error Icon" />
                      {error?.message}
                    </div>
                  )}
                </>
              );
            }}
          />
        </div>

        <div className="bg-[#FBF5F3] rounded-lg p-4">
          <p className="font-lg font-[EnergyBold] mb-4">+ Exclude</p>

          <Controller
            control={control}
            name="exclude"
            render={({ field, fieldState: { error } }) => {
              return (
                <>
                  <Autocomplete
                    multiple
                    {...field}
                    disabled={disabled}
                    value={
                      (field.value || []).filter(
                        (option: any) =>
                          !!dataSegment?.data?.find((i: any) => i?.segmentId === option)?.segmentName
                      )
                    }
                    options={dataSegment?.data?.map((i: any) => i?.segmentId)}
                    getOptionKey={(option: any) => option}
                    getOptionLabel={(option: any) =>{
                      return dataSegment?.data?.find((i: any) => i?.segmentId === option)?.segmentName
                    }}
                    onChange={(_event, value: any) => {
                      const valid = value.filter(
                        (option: any) =>
                          !!dataSegment?.data?.find((i: any) => i?.segmentId === option)?.segmentName
                      );
                      field.onChange(valid);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Select one or more segments"
                      />
                    )}
                    slotProps={{
                      chip: {
                        size: "small",
                      }
                    }}
                  />

                  {error?.message && (
                    <div className="create-segment__input-wrap__segment-err">
                      <img src={errorIcon} alt="Error Icon" />
                      {error?.message}
                    </div>
                  )}
                </>
              );
            }}
          />
        </div>
        <div className="create-campaign__divider"></div>
        <div className="create-campaign__dropdown-wrap__label">
          Audience groups and destinations:
        </div>
        <div className="dropdown__filter-section mt-6">
          <div className="flex items-center justify-between" style={{ marginBottom: "18px" }}>
            <div className="flex items-center gap-2">
              <p className="font-lg font-[EnergyBold]">All audience members</p>
            </div>
          </div>
          {fields.map((item, index) => (
            <div key={item.id} className="form-control-container mb-3">
              <div className="dropdown__filter-wrap__container">
                <div className="dropdown__filter-wrap__fields">
                  <div className="dropdown__filter-wrap__fields__filter">
                    <label className="form-control-container__label">Audience group</label>
                    <Controller
                      name={`audienceGroups.${index}.audienceGroup`}
                      control={control}
                      rules={{
                        required: "Please provide audience group name",
                      }}
                      render={({ field, fieldState: { error } }) => (
                        <>
                          <TextField
                            {...field}
                            fullWidth
                            variant="outlined"
                            disabled={disabled}
                            placeholder="Add audience group name"
                            slotProps={{
                              input: {
                                inputProps: { maxLength: 100 },
                              },
                            }}
                            onChange={(e) => {
                              const value = e.target.value;

                              if (value.length === 100) {
                                setError(`audienceGroups.${index}.audienceGroup`, {
                                  type: "maxLength",
                                  message: "Audience group name cannot exceed 100 characters",
                                });
                              } else {
                                clearErrors(`audienceGroups.${index}.audienceGroup`);
                              }

                              field.onChange(value);
                            }}
                          />

                          {error?.message && (
                            <div className="create-campaign__input-wrap__segment-err">
                              <img src={errorIcon} alt="Error Icon" />
                              {error.message}
                            </div>
                          )}
                        </>
                      )}
                    />
                  </div>

                  <div className="dropdown__filter-wrap__fields__filter destination">
                    <label className="form-control-container__label">Destination</label>
                    <Controller
                      name={`audienceGroups.${index}.destinations`}
                      rules={{
                        required: "required",
                      }}
                      control={control}
                      render={({ field, fieldState: { error } }) => {
                        return (
                          <>
                            <Autocomplete
                              multiple
                              options={options}
                              disabled={disabled}
                              getOptionLabel={(option) => option.label as string}
                              isOptionEqualToValue={(option, value) => option.value === value.value}
                              value={options.filter((opt) => field.value?.includes(opt.value))}
                              onChange={(_event, selectedOptions) => {
                                const selectedIds = selectedOptions.map((opt) => opt.value);
                                field.onChange(selectedIds);
                              }}
                              renderInput={(params) => (
                                  <TextField
                                      {...params}
                                      variant="outlined"
                                      placeholder="Destinations"
                                      disabled={isLoading || disabled}
                                />
                              )}
                              ChipProps={{
                                size: "small",
                              }}
                              renderTags={(tagValue, getTagProps) =>
                                tagValue.map((option: any, index: number) => (
                                  <Chip
                                    {...getTagProps({ index })}
                                    key={option.value}
                                    label={option.label}
                                    size="small"
                                    sx={{
                                      ...(option.status === "failed" && {
                                        backgroundColor: "#facfda",
                                      }),
                                    }}
                                  />
                                ))
                              }
                            />

                            {error?.message && (
                              <div className="create-campaign__input-wrap__segment-err">
                                <img src={errorIcon} alt="Error Icon" />
                                {error.message}
                              </div>
                            )}
                            {(() => {
                              const failedDests = options.filter(
                                (o) => o.status === "failed" && field.value?.includes(o.value)
                              );
                              return failedDests.length > 0 ? (
                                <div style={{ color: "#e70070", fontSize: "0.8rem", marginTop: "4px" }}>
                                  {failedDests.map((d) => (
                                    <div key={d.value}>Destination warning: {d.status_text || "status failed"} for {d.label}</div>
                                  ))}
                                </div>
                              ) : null;
                            })()}
                          </>
                        );
                      }}
                    />
                  </div>

                  <div className="dropdown__filter-wrap__fields__filter">
                    <label className="form-control-container__label">Split in %</label>
                    <Controller
                      name={`audienceGroups.${index}.splitPercentage`}
                      control={control}
                      rules={{
                        required: "required",
                      }}
                      render={({ field, fieldState: { error } }) => {
                        const totalSplit = watch("audienceGroups").reduce(
                          (accumulator: any, split: any) => {
                            return accumulator + Number(split.splitPercentage);
                          },
                          0
                        );

                        return (
                          <>
                            <TextField
                              {...field}
                              type="number"
                              fullWidth
                              variant="outlined"
                              disabled={disabled}
                              slotProps={{
                                input: {
                                  inputProps: {
                                    inputMode: "numeric",
                                    pattern: "[0-9]*", // only digits
                                    min: 0,
                                  },
                                },
                              }}
                              onChange={(e) => {
                                // strip non-digit characters
                                const value = e.target.value.replace(/\D/g, "");

                                field.onChange(value);
                              }}
                            />

                            {totalSplit > 100 && (
                              <div className="create-segment__input-wrap__segment-err">
                                <img src={errorIcon} alt="Error Icon" />
                                Group splits must add up to 100%
                              </div>
                            )}

                            {error?.message && (
                              <div className="create-campaign__input-wrap__segment-err">
                                <img src={errorIcon} alt="Error Icon" />
                                {error.message}
                              </div>
                            )}
                          </>
                        );
                      }}
                    />
                  </div>
                </div>
                {!disabled && (
                  <div
                    className="dropdown__filter-wrap__action"
                    style={{
                      cursor: "pointer",
                      color: "#F1181B",
                      display: "flex",
                      alignItems: "center",
                    }}
                    onClick={() => remove(index)}
                  >
                    {deleteRowTooltip && (
                      <CustomTooltip title={deleteRowTooltip.tooltip}>
                        <img src={deleteRowTooltip.icon} alt="deleteRowTooltip.alt" style={{ cursor: "pointer" }} />
                      </CustomTooltip>
                    )}
                  </div>
                )}
              </div>
              {/* <Controller
                name={`audienceGroups.${index}.makeControl`}
                control={control}
                render={({ field }) => (
                  <CustomDateCheckbox
                    {...field}
                    label="Make control"
                    className="create-campaign__field-btm-label"
                    checked={field.value}
                    onChange={(e) => {
                      const updatedFields = getValues("audienceGroups").map(
                        (field: any, indexField: any) => {
                          if (indexField === index) {
                            return {
                              ...field,
                              makeControl: e,
                            };
                          } else {
                            return {
                              ...field,
                              makeControl: false,
                            };
                          }
                        }
                      );
                      replace(updatedFields);
                    }}
                  />
                )}
              /> */}
            </div>
          ))}

          <div className="create-segment__divider"></div>
          {getValues("audienceGroups")?.length < 5 && !disabled && (
            <div
              className="flex items-center cursor-pointer mb-3"
              onClick={() =>
                append({
                  makeControl: false,
                  destinations: [],
                  audienceGroup: "",
                  splitPercentage: "",
                })
              }
            >
              <img src={addCircleIcon} alt="Add" />
              <span className="text-[#E70070] font-bold ml-2">Add group</span>
            </div>
          )}

          {getValues("audienceGroups")?.length === 5 && (
            <div className="create-campaign__input-wrap__segment-err">
              Maximum 5 audience groups can be added.
            </div>
          )}
        </div>
        <div className="create-campaign__divider"></div>
        <div className="dropdown__filter-section mt-6">
          <div className="flex items-center justify-between" style={{ marginBottom: "18px" }}>
            <div className="flex items-center gap-2">
              <p className="font-lg font-[EnergyBold]">Timing</p>
            </div>
          </div>
          <div className="form-control-container">
            <div className="dropdown__filter-wrap__container">
              <div className="dropdown__filter-wrap__fields">
                <div className="dropdown__filter-wrap__fields__filter">
                  <label className="form-control-container__label">Campaign type</label>
                  <Controller
                    name="campaignType"
                    rules={{
                      required: "required",
                    }}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <Select
                          {...field}
                          displayEmpty
                          disabled={disabled}
                          onChange={(e) => {
                            setValue("deliveryType", "");
                            setValue("startDate", null);
                            setValue("timeValue", null);
                            setValue("endDate", null);
                            setValue("quantityValue", "");

                            field.onChange(e.target.value);
                          }}
                          className="form-control-container__select"
                        >
                          <MenuItem value="" disabled>
                            Select type
                          </MenuItem>
                          {types.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>

                        {error?.message && (
                          <div className="create-campaign__input-wrap__segment-err">
                            <img src={errorIcon} alt="Error Icon" />
                            {error?.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>

                {watch("campaignType") === "One-time" && (
                  <div className="dropdown__filter-wrap__fields__filter">
                    <label className="form-control-container__label">Delivery type</label>
                    <Controller
                      name="deliveryType"
                      control={control}
                      rules={{
                        required: "required",
                      }}
                      render={({ field, fieldState: { error } }) => {
                        return (
                          <>
                            <Select
                              {...field}
                              displayEmpty
                              disabled={disabled}
                              onChange={(e) => {
                                setValue("startDate", null);
                                setValue("timeValue", null);

                                field.onChange(e.target.value);
                              }}
                              className="form-control-container__select"
                            >
                              <MenuItem value="" disabled>
                                Select type
                              </MenuItem>
                              {deliveryTypes.map((type) => (
                                <MenuItem key={type} value={type}>
                                  {type}
                                </MenuItem>
                              ))}
                            </Select>

                            {error?.message && (
                              <div className="create-campaign__input-wrap__segment-err">
                                <img src={errorIcon} alt="Error Icon" />
                                {error?.message}
                              </div>
                            )}
                          </>
                        );
                      }}
                    />
                  </div>
                )}

                {watch("campaignType") &&
                  isHaveDeliveryType &&
                  watch("deliveryType") !== "Deliver ASAP" && (
                    <div className="dropdown__filter-wrap__fields__filter dropdown__filter-wrap__fields__calendar">
                      <Controller
                        control={control}
                        name="startDate"
                        rules={{
                          required: "required",
                        }}
                        render={({ field, fieldState: { error } }) => {
                          return (
                            <>
                              <label className="form-control-container__label">
                                Start Date
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <DateField
                                    {...field}
                                    disabled={disabled}
                                    value={field.value ? dayjs(field.value) : null}
                                    onChange={(e) => field.onChange(dayjs(e).format("YYYY-MM-DD"))}
                                    placeholder="DD/MM/YYYY"
                                    minDate={dayjs()}
                                  />
                                </LocalizationProvider>
                              </label>

                              {error?.message && (
                                <div className="create-segment__input-wrap__segment-err">
                                  <img src={errorIcon} alt="Error Icon" />
                                  {error?.message}
                                </div>
                              )}
                            </>
                          );
                        }}
                      />
                    </div>
                  )}

                {watch("campaignType") &&
                  isHaveDeliveryType &&
                  watch("deliveryType") !== "Deliver ASAP" && (
                    <div className="dropdown__filter-wrap__fields__filter">
                      <Controller
                        name="timeValue"
                        control={control}
                        rules={{
                          required: "Time is required",
                          validate: (value) => {
                            if (!value) return "Time is required";
                            const selectedTime = dayjs(value, "HH:mm:ss");
                            const selectedDate = watch("startDate");
                            if (!selectedDate) return "Please select a start date first";
                            if (
                              dayjs(selectedDate).isSame(dayjs(), "day") &&
                              selectedTime.isBefore(dayjs())
                            ) {
                              return "Selected time cannot be in the past";
                            }
                            return true;
                          },
                        }}
                        render={({ field, fieldState: { error } }) => {
                          const now = dayjs();
                          const selectedDate = watch("startDate");
                          const isToday = selectedDate
                            ? dayjs(selectedDate).isSame(now, "day")
                            : false;

                          const shouldDisableTime = (
                            value: Dayjs | null,
                            view: "hours" | "minutes" | "seconds"
                          ) => {
                            if (!selectedDate || !value) return true; // disable if no date selected
                            if (!isToday) return false; // allow all times for non-today dates

                            const currentHour = now.hour();
                            const currentMinute = now.minute();
                            const currentSecond = now.second();

                            const hour = value.hour();
                            const minute = value.minute();
                            const second = value.second();

                            if (view === "hours") {
                              // disable past hours
                              if (hour < currentHour) return true;
                              // disable all AM hours if current time is PM
                              if (now.format("A") === "PM" && hour < 12) return true;
                              // additionally, disable all past AM hours from current PM
                              if (now.format("A") === "AM" && hour < currentHour) return true;
                            }

                            if (view === "minutes") {
                              // disable minutes for AM hours as well
                              if (hour < currentHour) return true;
                              return hour === currentHour && minute < currentMinute;
                            }


                            if (view === "seconds") {
                              return (
                                hour === currentHour &&
                                minute === currentMinute &&
                                second < currentSecond
                              );
                            }

                            return false;
                          };

                          return (
                            <>
                              <label className="form-control-container__label">Time value</label>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker
                                  ampm
                                  value={field.value ? dayjs(field.value, "HH:mm:ss") : isToday ? now.add(5, "minute")
                                    : null}
                                  onChange={(e) => {
                                    const value = e ? dayjs(e).format("HH:mm:ss") : "";
                                    field.onChange(value);

                                    const selectedDate = watch("startDate");
                                    const now = dayjs();
                                    const isToday = selectedDate ? dayjs(selectedDate).isSame(now, "day") : false;

                                    // manually trigger validation
                                    if (!value) {
                                      setError("timeValue", { type: "required", message: "Time is required" });
                                    } else if (!selectedDate) {
                                      setError("timeValue", { type: "validate", message: "Please select a start date first" });
                                    } else if (isToday && dayjs(value, "HH:mm:ss").isBefore(now)) {
                                      setError("timeValue", { type: "validate", message: "Selected time cannot be in the past" });
                                    } else {
                                      clearErrors("timeValue");
                                    }
                                  }}

                                  shouldDisableTime={shouldDisableTime}
                                  disabled={!selectedDate || disabled} // disable if no start date
                                  openTo="hours"
                                  ampmInClock
                                  enableAccessibleFieldDOMStructure={false}
                                />
                              </LocalizationProvider>

                              {error?.message && (
                                <div className="create-campaign__input-wrap__segment-err">
                                  <img src={errorIcon} alt="Error Icon" />
                                  {error.message}
                                </div>
                              )}
                            </>
                          );
                        }}
                      />
                    </div>
                  )}

                {watch("campaignType") &&
                  isHaveDeliveryType &&
                  watch("deliveryType") !== "Deliver ASAP" &&
                  watch("deliveryType") !== "Schedule delivery" && (
                    <div className="dropdown__filter-wrap__fields__filter dropdown__filter-wrap__fields__calendar">
                      <div
                        style={{
                          display: watch("afterSpecificNumberOfOccurrences") ? "block" : "none",
                        }}
                      >
                        <Controller
                          name="quantityValue"
                          rules={{
                            required: watch("afterSpecificNumberOfOccurrences") ? "required" : false,
                            max: {
                              value: 365,
                              message: "Quantity cannot be more than 365",
                            },
                            min: {
                              value: 0,
                              message: "Quantity cannot be negative",
                            },
                          }}
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <label ref={field.ref} className="form-control-container__label">
                                Quantity Value
                                <TextField
                                  {...field}
                                  value={field.value || ""}
                                  type="number"
                                  disabled={disabled}
                                  placeholder="Add number"
                                  slotProps={{
                                    input: {
                                      style: {
                                        width: "100%",
                                      },
                                      inputProps: {
                                        inputMode: "numeric",
                                        pattern: "[0-9]*", // only digits
                                        min: 0,
                                      },
                                    },
                                  }}
                                  onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ""))}
                                />
                              </label>
                              {error?.message && (
                                <div className="create-segment__input-wrap__segment-err">
                                  <img src={errorIcon} alt="Error Icon" />
                                  {error?.message}
                                </div>
                              )}
                            </>
                          )}
                        />
                      </div>
                      <div
                        style={{
                          display: !watch("afterSpecificNumberOfOccurrences") ? "block" : "none",
                        }}
                      >
                        <Controller
                          name="endDate"
                          control={control}
                          rules={{
                            required: !watch("afterSpecificNumberOfOccurrences") ? "required" : false,
                          }}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <label className="form-control-container__label">
                                  End Date
                                  <DateField
                                    {...field}
                                    disabled={disabled}
                                    value={field.value ? dayjs(field.value) : null}
                                    onChange={(e) => field.onChange(dayjs(e).format("YYYY-MM-DD"))}
                                    placeholder="DD/MM/YYYY"
                                    minDate={dayjs(getValues("startDate"))}
                                  />
                                </label>
                              </LocalizationProvider>
                              {error?.message && (
                                <div className="create-campaign__input-wrap__segment-err">
                                  <img src={errorIcon} alt="Error Icon" />
                                  {error?.message}
                                </div>
                              )}
                            </>
                          )}
                        />
                      </div>
                      <Controller
                        name="afterSpecificNumberOfOccurrences"
                        control={control}
                        render={({ field, fieldState: { error } }) => {
                          return (
                            <>
                              <CustomDateCheckbox
                                label="After specific number of occurrences"
                                className="create-campaign__field-btm-label"
                                checked={field.value}
                                disabled={disabled}
                                onChange={(e) => {
                                  field.onChange(e);
                                  if (e) {
                                    setValue("endDate", null);
                                  } else {
                                    setValue("quantityValue", "");
                                  }
                                }}
                              />

                              {error?.message && (
                                <div className="create-campaign__input-wrap__segment-err">
                                  <img src={errorIcon} alt="Error Icon" />
                                  {error?.message}
                                </div>
                              )}
                            </>
                          );
                        }}
                      />
                    </div>
                  )}
              </div>
            </div>
          </div>
          <DelieryViewTime />
        </div>
      </div>
    </>
  );
};

export default CampaignFormComponent;
